// ============================================================
// ByOurConsent.com -- Cart Page
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import useWindowWidth from '../hooks/useWindowWidth';
import toast  from 'react-hot-toast';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

// Product images can be either a local relative path (/uploads/...)
// or a full absolute URL (e.g. Printful's CDN) -- only prepend our
// backend origin for the former.
// Derives the backend origin from the same env var api.js uses, so
// production, dev, and any future domain/port all stay in sync from
// one place. Falls back to the documented dev port if unset.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5002/api').replace(/\/api\/?$/, '');
const imageUrl = (path) => path ? (path.startsWith('http') ? path : `${API_ORIGIN}${path}`) : path;

const FREE_SHIPPING_THRESHOLD = 50.00;
const SHIPPING_COST            = 5.99;

// ============================================================
// Cart item row
// ============================================================
const CartItem = ({ item, onUpdateQty, onRemove }) => {
    const width    = useWindowWidth();
    const isMobile = width < 768;
    return (
    <div style={{
        display:      'grid',
        gridTemplateColumns: isMobile ? '60px 1fr' : '80px 1fr auto',
        gap:          '16px',
        padding:      '16px 0',
        borderBottom: '0.5px solid #c8beaa',
        alignItems:   'center'
    }}>
        {/* Image */}
        <div style={{
            width:        '80px',
            height:       '80px',
            background:   '#ede8dc',
            borderRadius: '3px',
            border:       '0.5px solid #c8beaa',
            overflow:     'hidden',
            display:      'flex',
            alignItems:   'center',
            justifyContent: 'center'
        }}>
            {item.image_url ? (
                <img
                    src={imageUrl(item.image_url)}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }}
                />
            ) : (
                <span style={{
                    fontSize:   '9px',
                    fontFamily: 'sans-serif',
                    color:      '#8a8070',
                    textAlign:  'center',
                    padding:    '4px'
                }}>
                    BOC
                </span>
            )}
        </div>

        {/* Details */}
        <div>
            <div style={{
                fontSize:     '15px',
                fontWeight:   600,
                color:        '#1e2a3a',
                fontFamily:   "'EB Garamond', Georgia, serif",
                marginBottom: '3px'
            }}>
                {item.name}
            </div>
            {(item.size || item.color) && (
                <div style={{
                    fontSize:     '12px',
                    fontFamily:   'sans-serif',
                    color:        '#6a7080',
                    marginBottom: '8px'
                }}>
                    {[item.size, item.color].filter(Boolean).join(' / ')}
                </div>
            )}
            {/* Quantity controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                <button
                    onClick={() => onUpdateQty(item, item.quantity - 1)}
                    style={{
                        background:   '#ede8dc',
                        border:       '0.5px solid #c8beaa',
                        borderRight:  'none',
                        width:        '28px',
                        height:       '28px',
                        cursor:       'pointer',
                        fontSize:     '14px',
                        color:        '#1e2a3a',
                        borderRadius: '3px 0 0 3px'
                    }}>
                    −
                </button>
                <div style={{
                    width:          '36px',
                    height:         '28px',
                    border:         '0.5px solid #c8beaa',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    fontSize:       '13px',
                    fontFamily:     'sans-serif',
                    color:          '#1e2a3a',
                    background:     '#f5f0e8'
                }}>
                    {item.quantity}
                </div>
                <button
                    onClick={() => onUpdateQty(item, item.quantity + 1)}
                    style={{
                        background:   '#ede8dc',
                        border:       '0.5px solid #c8beaa',
                        borderLeft:   'none',
                        width:        '28px',
                        height:       '28px',
                        cursor:       'pointer',
                        fontSize:     '14px',
                        color:        '#1e2a3a',
                        borderRadius: '0 3px 3px 0'
                    }}>
                    +
                </button>
                <button
                    onClick={() => onRemove(item)}
                    style={{
                        background:    'transparent',
                        border:        'none',
                        color:         '#8a8070',
                        fontSize:      '11px',
                        fontFamily:    'sans-serif',
                        cursor:        'pointer',
                        marginLeft:    '12px',
                        textDecoration:'underline'
                    }}>
                    Remove
                </button>
            </div>
        </div>

        {/* Price */}
        <div style={{
            fontSize:   '16px',
            fontWeight: 600,
            color:      '#1e2a3a',
            fontFamily: "'EB Garamond', Georgia, serif",
            textAlign:  'right',
            whiteSpace: 'nowrap'
        }}>
            ${(parseFloat(item.price) * item.quantity).toFixed(2)}
        </div>
    </div>
    );
};

// ============================================================
// CartPage
// ============================================================
const CartPage = () => {
    const navigate        = useNavigate();
    const width           = useWindowWidth();
    const isMobile        = width < 768;
    const { cart, updateQty, removeItem, clearCart } = useCart();
    const { user }        = useAuth();
    const [coupon, setCoupon] = useState('');
    const [couponApplied, setCouponApplied] = useState(null);

    const handleUpdateQty = (item, newQty) => {
        if (newQty < 1) { handleRemove(item); return; }
        if (newQty > 10) return;
        updateQty(item.product_id, item.variant_id, newQty);
    };

    const handleRemove = (item) => {
        removeItem(item.product_id, item.variant_id);
        toast.success('Item removed');
    };

    const handleClearCart = () => {
        clearCart();
        toast.success('Cart cleared');
    };

    const subtotal = cart.reduce(
        (sum, item) => sum + parseFloat(item.price) * item.quantity, 0
    );

    const freeShipping   = subtotal >= FREE_SHIPPING_THRESHOLD;
    const shippingCost   = cart.length === 0 ? 0 : freeShipping ? 0 : SHIPPING_COST;
    const discountAmount = couponApplied ? subtotal * 0.10 : 0;
    const total          = subtotal - discountAmount + shippingCost;
    const toFreeShipping = FREE_SHIPPING_THRESHOLD - subtotal;

    const hasPhysical = cart.some(i => i.product_type === 'physical');

    return (
        <>
            <Helmet>
                <title>Cart — By Our Consent</title>
            </Helmet>

            <Nav cartCount={cart.reduce((s, i) => s + i.quantity, 0)} />

            {/* Header */}
            <div style={{
                background:   '#1e2a3a',
                padding:      '1.25rem 1.5rem',
                borderBottom: '2px solid #8b1a1a'
            }}>
                <div style={{ maxWidth: '860px', margin: '0 auto' }}>
                    <h1 style={{
                        fontSize:   '28px',
                        fontWeight: 600,
                        color:      '#e8dfc8',
                        fontFamily: "'EB Garamond', Georgia, serif",
                        margin:     0
                    }}>
                        Your cart
                    </h1>
                </div>
            </div>

            <div style={{
                maxWidth: '860px',
                margin:   '0 auto',
                padding:  '1.5rem'
            }}>
                {cart.length === 0 ? (
                    /* Empty cart */
                    <div style={{
                        textAlign:  'center',
                        padding:    '4rem 2rem',
                        background: '#f5f0e8',
                        border:     '0.5px solid #c8beaa',
                        borderRadius:'4px'
                    }}>
                        <div style={{
                            fontSize:     '48px',
                            marginBottom: '1rem'
                        }}>
                            🛒
                        </div>
                        <div style={{
                            fontSize:     '20px',
                            fontWeight:   600,
                            color:        '#1e2a3a',
                            fontFamily:   "'EB Garamond', Georgia, serif",
                            marginBottom: '8px'
                        }}>
                            Your cart is empty
                        </div>
                        <div style={{
                            fontSize:     '13px',
                            fontFamily:   'sans-serif',
                            color:        '#6a7080',
                            marginBottom: '1.5rem'
                        }}>
                            Wear the movement — browse our store
                        </div>
                        <Link to="/store" style={{
                            background:    '#8b1a1a',
                            color:         '#f5f0e8',
                            padding:       '10px 24px',
                            fontSize:      '12px',
                            fontFamily:    'sans-serif',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            textDecoration:'none',
                            borderRadius:  '3px',
                            display:       'inline-block'
                        }}>
                            Browse store
                        </Link>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 300px',
                        gap:     '2rem',
                        alignItems: 'start'
                    }}>
                        {/* Cart items */}
                        <div>
                            {/* Free shipping progress */}
                            {hasPhysical && !freeShipping && (
                                <div style={{
                                    background:   '#f5f0e8',
                                    border:       '0.5px solid #c8beaa',
                                    borderRadius: '3px',
                                    padding:      '10px 14px',
                                    marginBottom: '1rem',
                                    fontSize:     '12px',
                                    fontFamily:   'sans-serif',
                                    color:        '#4a5060'
                                }}>
                                    <div style={{ marginBottom: '6px' }}>
                                        Add <strong style={{ color: '#3b6d11' }}>
                                            ${toFreeShipping.toFixed(2)}
                                        </strong> more for free shipping
                                    </div>
                                    <div style={{
                                        background:   '#c8beaa',
                                        borderRadius: '10px',
                                        height:       '4px',
                                        overflow:     'hidden'
                                    }}>
                                        <div style={{
                                            background:   '#3b6d11',
                                            height:       '100%',
                                            width:        `${Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100)}%`,
                                            borderRadius: '10px',
                                            transition:   'width 0.3s'
                                        }} />
                                    </div>
                                </div>
                            )}

                            {freeShipping && hasPhysical && (
                                <div style={{
                                    background:   '#f0f7ec',
                                    border:       '0.5px solid #3b6d11',
                                    borderRadius: '3px',
                                    padding:      '10px 14px',
                                    marginBottom: '1rem',
                                    fontSize:     '12px',
                                    fontFamily:   'sans-serif',
                                    color:        '#3b6d11',
                                    fontWeight:   600
                                }}>
                                    ✓ You qualify for free shipping!
                                </div>
                            )}

                            {/* Items */}
                            {cart.map((item, i) => (
                                <CartItem
                                    key={`${item.product_id}-${item.variant_id}-${i}`}
                                    item={item}
                                    onUpdateQty={handleUpdateQty}
                                    onRemove={handleRemove}
                                />
                            ))}

                            {/* Clear cart */}
                            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                                <button
                                    onClick={handleClearCart}
                                    style={{
                                        background:    'transparent',
                                        border:        'none',
                                        color:         '#8a8070',
                                        fontSize:      '11px',
                                        fontFamily:    'sans-serif',
                                        cursor:        'pointer',
                                        textDecoration:'underline'
                                    }}>
                                    Clear cart
                                </button>
                            </div>

                            {/* Continue shopping */}
                            <div style={{ marginTop: '1rem' }}>
                                <Link to="/store" style={{
                                    fontSize:      '12px',
                                    fontFamily:    'sans-serif',
                                    color:         '#8b6a3a',
                                    textDecoration:'none',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase'
                                }}>
                                    ← Continue shopping
                                </Link>
                            </div>
                        </div>

                        {/* Order summary */}
                        <div style={{
                            background:   '#f5f0e8',
                            border:       '0.5px solid #c8beaa',
                            borderRadius: '4px',
                            padding:      '1.25rem',
                            position:     'sticky',
                            top:          '72px'
                        }}>
                            <div style={{
                                fontSize:      '14px',
                                fontWeight:    600,
                                color:         '#1e2a3a',
                                marginBottom:  '14px',
                                paddingBottom: '10px',
                                borderBottom:  '0.5px solid #c8beaa'
                            }}>
                                Order summary
                            </div>

                            {/* Line items */}
                            {[
                                { label: 'Subtotal', value: `$${subtotal.toFixed(2)}` },
                                {
                                    label: 'Shipping',
                                    value: freeShipping ? 'FREE' : cart.length === 0 ? '—' : `$${shippingCost.toFixed(2)}`,
                                    green: freeShipping
                                },
                                ...(couponApplied ? [{
                                    label: `Coupon (${couponApplied})`,
                                    value: `-$${discountAmount.toFixed(2)}`,
                                    green: true
                                }] : [])
                            ].map(({ label, value, green }) => (
                                <div key={label} style={{
                                    display:        'flex',
                                    justifyContent: 'space-between',
                                    marginBottom:   '8px',
                                    fontSize:       '13px',
                                    fontFamily:     'sans-serif',
                                    color:          green ? '#3b6d11' : '#4a5060'
                                }}>
                                    <span>{label}</span>
                                    <span style={{ fontWeight: green ? 600 : 400 }}>{value}</span>
                                </div>
                            ))}

                            {/* Total */}
                            <div style={{
                                display:        'flex',
                                justifyContent: 'space-between',
                                padding:        '10px 0',
                                borderTop:      '0.5px solid #c8beaa',
                                borderBottom:   '0.5px solid #c8beaa',
                                marginBottom:   '14px',
                                fontSize:       '16px',
                                fontFamily:     "'EB Garamond', Georgia, serif",
                                fontWeight:     600,
                                color:          '#1e2a3a'
                            }}>
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>

                            {/* Coupon */}
                            {!couponApplied && (
                                <div style={{ marginBottom: '14px' }}>
                                    <div style={{ display: 'flex', gap: '0' }}>
                                        <input
                                            type="text"
                                            value={coupon}
                                            onChange={e => setCoupon(e.target.value.toUpperCase())}
                                            placeholder="Coupon code"
                                            style={{
                                                flex:         1,
                                                background:   '#f5f0e8',
                                                border:       '0.5px solid #c8beaa',
                                                borderRight:  'none',
                                                padding:      '8px 10px',
                                                fontSize:     '12px',
                                                fontFamily:   'sans-serif',
                                                color:        '#1e2a3a',
                                                outline:      'none',
                                                borderRadius: '3px 0 0 3px'
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                if (coupon === 'MOVEMENT10') {
                                                    setCouponApplied('MOVEMENT10');
                                                    toast.success('Coupon applied — 10% off!');
                                                } else {
                                                    toast.error('Invalid coupon code');
                                                }
                                            }}
                                            style={{
                                                background:    '#1e2a3a',
                                                color:         '#e8dfc8',
                                                border:        'none',
                                                padding:       '8px 12px',
                                                fontSize:      '11px',
                                                fontFamily:    'sans-serif',
                                                cursor:        'pointer',
                                                borderRadius:  '0 3px 3px 0',
                                                whiteSpace:    'nowrap'
                                            }}>
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Checkout button */}
                            <button
                                onClick={() => user 
                                    ? navigate('/checkout')
                                    : navigate('/register', { state: { from: '/checkout', message: 'Create a free account to complete your order and track its status.' } })
                                }
                                style={{
                                    width:         '100%',
                                    background:    '#8b1a1a',
                                    color:         '#f5f0e8',
                                    border:        'none',
                                    padding:       '13px',
                                    fontSize:      '13px',
                                    fontFamily:    'sans-serif',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    cursor:        'pointer',
                                    borderRadius:  '3px',
                                    marginBottom:  '10px'
                                }}>
                                Proceed to checkout
                            </button>

                            {/* Trust signals */}
                            <div style={{
                                textAlign:  'center',
                                fontSize:   '11px',
                                fontFamily: 'sans-serif',
                                color:      '#8a8070',
                                lineHeight: 1.6
                            }}>
                                🔒 Secure checkout via Stripe
                                <br />
                                No account required to purchase
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default CartPage;
